import { warn } from '@execonline-inc/logging';
import { assertNever, noop } from '@kofno/piper';
import SegmentStore from '.';
import { segmentRoute } from '../ClientRoutes';
import ErrorActionableReaction, { EAProps } from '../ErrorActionableReaction';
import { nav } from '../Navigation';
import ProgramStore from '../ProgramStore';
import { windowScrollToElement, windowScrollTopOffset } from '../Window';
import { SegmentState } from './Types';

interface Props extends EAProps<SegmentStore> {
  programStore: ProgramStore;
}

class ProgramSegmentReactions extends ErrorActionableReaction<SegmentStore, SegmentState, Props> {
  tester = () => this.props.store.state;

  effect = (state: SegmentState) => {
    const { programStore } = this.props;
    switch (state.kind) {
      case 'advancing-to':
        programStore.programDetailResource.map(programStore.reloading);
        break;
      case 'loaded':
        // Frame the segment content once it has loaded — runs after render (so the target is
        // correct) and covers every navigation path (outline click, Next/Previous, initial
        // load), which fixes framing not re-applying after an error.
        windowScrollToElement('segment-column').fork(
          (e) => warn('Unable to scroll to segment content', e),
          noop,
        );
        break;
      case 'ready': {
        // Advancing swaps the new segment into the store in place, without touching
        // the router, so the address bar has to be synced here or a mid-course
        // refresh reloads whichever segment the student first landed on. Scoping
        // comes from the mount point: this reaction only renders on the full
        // segment page (via SegmentPanel), never in the modal or embedded
        // streamlined flows, whose URLs must not be rewritten.
        const urlSegmentId = globalThis.location.pathname.split('/').findLast(Boolean);
        if (urlSegmentId !== state.segmentResource.payload.id.toString()) {
          nav(segmentRoute(state.segmentResource.payload));
        }
        break;
      }
      case 'quiz-failed':
      case 'loading':
      case 'reporting-results':
      case 'completing':
      case 'advancing':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'processing-request':
      case 'waiting':
      case 'schedule-session':
        break;
      case 'error':
        // A segment that fails to load surfaces its error as a flash notification at the top
        // of the column; scroll to the top of the page so it's never left off-screen (e.g.
        // after the navigation frame-scroll).
        windowScrollTopOffset(0).fork((e) => warn('Unable to scroll to top of window', e), noop);
        break;
      default:
        assertNever(state);
    }
  };
}

export default ProgramSegmentReactions;
